import React, { useEffect, useRef, useState } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { Smartphone, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';

/**
 * Renders a discrete "Get it on your phone" trigger that opens a popover with a QR code.
 * The QR encodes the production site URL with `?source=qr` so installs can be tracked later.
 *
 * Hidden on small screens (< 640px) since users on mobile already have the install button.
 */
export default function PWAQrCode({ url, className = '' }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef(null);

  // Default to current origin (production: flexi-location.be) with the install
  // path so the QR scan lands on a clean install wizard, not the marketing home.
  const target =
    url ||
    `${typeof window !== 'undefined' ? window.location.origin : 'https://flexi-location.be'}/install?source=qr`;

  useEffect(() => {
    if (!open) return;
    const onClick = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => e.key === 'Escape' && setOpen(false);
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <div ref={wrapperRef} className={`relative hidden sm:inline-block ${className}`}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        data-testid="pwa-qr-trigger"
        className="inline-flex items-center gap-1.5 text-xs text-white/70 hover:text-white transition-colors underline-offset-4 hover:underline"
      >
        <Smartphone size={14} />
        <span>{t('pwa.qrTrigger', 'Télécharger sur mobile')}</span>
      </button>

      {open && (
        <div
          data-testid="pwa-qr-popover"
          role="dialog"
          className="absolute bottom-full right-0 mb-3 z-50 w-64 bg-white text-[#1A3C34] rounded-lg shadow-2xl border border-black/5 p-4 animate-in fade-in slide-in-from-bottom-2 duration-200"
        >
          <button
            type="button"
            onClick={() => setOpen(false)}
            data-testid="pwa-qr-close"
            aria-label="Close"
            className="absolute top-2 right-2 text-[#1A3C34]/40 hover:text-[#1A3C34]"
          >
            <X size={16} />
          </button>
          <h4 className="font-heading text-sm font-bold mb-1 pr-4">
            {t('pwa.qrTitle', 'Installez Flexi sur votre téléphone')}
          </h4>
          <p className="text-xs text-[#1A3C34]/60 mb-3 leading-relaxed">
            {t(
              'pwa.qrSubtitle',
              "Scannez ce code avec l'appareil photo de votre téléphone."
            )}
          </p>
          <div className="flex justify-center bg-[#F9F9F7] rounded-md p-3">
            <QRCodeSVG
              value={target}
              size={176}
              bgColor="#F9F9F7"
              fgColor="#1A3C34"
              level="M"
              includeMargin={false}
            />
          </div>
          <p className="text-[10px] text-[#1A3C34]/40 mt-2 text-center break-all">{target}</p>
        </div>
      )}
    </div>
  );
}
